import { useEffect, useMemo } from "react";
import liveChatButtonIconUrl from '@resources/img/theme/live-chat-icon.svg?url';

const liveChatButtonStyleId = 'aow-live-chat-ai-button-style'
const liveChatLabMobileBreakpoint = 1053
const liveChatFullScreenBreakpoint = 767
const liveChatMobileOffset = 16
const liveChatDesktopOffset = 24
const liveChatLabTopOffset = 80
const liveChatMobileBottomOffsetCssVariable = '--aow-live-chat-mobile-bottom-offset'
const liveChatRootZIndex = 1190
const liveChatButtonZIndex = liveChatRootZIndex + 1
const liveChatPanelZIndex = liveChatButtonZIndex + 1
const liveChatButtonSelector = [
  '#live-chat-ai-button',
  '.live-chat-ai-button',
].join(',\n')
const liveChatButtonContentSelector = [
  '#live-chat-ai-button svg',
  '#live-chat-ai-button img',
  '#live-chat-ai-button canvas',
  '.live-chat-ai-button svg',
  '.live-chat-ai-button img',
  '.live-chat-ai-button canvas',
  '.live-chat-ai-animation-canvas',
].join(',\n')
const liveChatButtonHoverSelector = [
  '#live-chat-ai-button:hover',
  '.live-chat-ai-button:hover',
].join(',\n')
const liveChatFloatingControlSelector = [
  '#live-chat-ai-button',
  '.live-chat-ai-button',
  '.live-chat-ai-animation-canvas',
  '.live-chat-ai-button-count',
].join(',\n')
const liveChatIframeSelector = [
  '#live-chat-ai-iframe',
  '.live-chat-ai-iframe',
].join(',\n')
const liveChatIframeWrapperSelector = [
  '#live-chat-ai-iframe-wrapper',
  '.live-chat-ai-iframe-wrapper',
].join(',\n')
const liveChatFullScreenPanelSelector = [
  liveChatIframeSelector,
  liveChatIframeWrapperSelector,
].join(',\n')
const liveChatBubbleSelector = [
  '#live-chat-ai-teaser-message-wrapper',
  '.live-chat-ai-teaser-message-wrapper',
  '#live-chat-ai-new-message-notification-wrapper',
  '.live-chat-ai-new-message-notification-wrapper',
].join(',\n')
const liveChatEmbedSelector = [
  '.live-chat-ai-wrapper',
  '#live-chat-ai-button',
  '.live-chat-ai-button',
  '.live-chat-ai-animation-canvas',
  '.live-chat-ai-button-count',
  '#live-chat-ai-iframe-wrapper',
  '.live-chat-ai-iframe-wrapper',
  '#live-chat-ai-iframe',
  '.live-chat-ai-iframe',
].join(',')

interface Props {
  hideOnLabMobile?: boolean
  side?: 'left' | 'right'
  vertical?: 'top' | 'bottom'
}

function getLiveChatButtonCss(props: Props) {
  const side = props.side ?? 'right'
  const vertical = props.vertical ?? 'bottom'
  const transformOrigin = vertical + ' ' + side
  const mobileBottomOffset = `var(${liveChatMobileBottomOffsetCssVariable}, ${liveChatMobileOffset}px)`
  const horizontalPosition = side === 'left'
    ? `
${liveChatFloatingControlSelector} {
  left: ${liveChatDesktopOffset}px !important;
  right: auto !important;
}

${liveChatIframeSelector} {
  left: ${liveChatDesktopOffset}px !important;
  right: auto !important;
}

${liveChatIframeWrapperSelector},
${liveChatBubbleSelector} {
  left: ${liveChatDesktopOffset}px !important;
  right: auto !important;
}

@media (max-width: ${liveChatLabMobileBreakpoint}px) {
  ${liveChatFloatingControlSelector},
  ${liveChatIframeSelector},
  ${liveChatIframeWrapperSelector},
  ${liveChatBubbleSelector} {
    left: ${liveChatMobileOffset}px !important;
    right: auto !important;
  }
}
`
    : `
${liveChatFloatingControlSelector} {
  left: auto !important;
  right: ${liveChatDesktopOffset}px !important;
}

${liveChatIframeSelector} {
  left: auto !important;
  right: ${liveChatDesktopOffset}px !important;
}

${liveChatIframeWrapperSelector},
${liveChatBubbleSelector} {
  left: auto !important;
  right: ${liveChatDesktopOffset}px !important;
}

@media (max-width: ${liveChatLabMobileBreakpoint}px) {
  ${liveChatFloatingControlSelector},
  ${liveChatIframeSelector},
  ${liveChatIframeWrapperSelector},
  ${liveChatBubbleSelector} {
    left: auto !important;
    right: ${liveChatMobileOffset}px !important;
  }
}
`
  const verticalPosition = vertical === 'top'
    ? `
${liveChatFloatingControlSelector},
${liveChatIframeSelector},
${liveChatIframeWrapperSelector},
${liveChatBubbleSelector} {
  top: ${liveChatLabTopOffset}px !important;
  bottom: auto !important;
}

${liveChatIframeWrapperSelector},
${liveChatBubbleSelector} {
  transform-origin: ${transformOrigin} !important;
}

@media (max-width: ${liveChatLabMobileBreakpoint}px) {
  ${liveChatFloatingControlSelector},
  ${liveChatIframeSelector},
  ${liveChatIframeWrapperSelector},
  ${liveChatBubbleSelector} {
    top: ${liveChatMobileOffset}px !important;
    bottom: auto !important;
  }
}
`
    : `
${liveChatFloatingControlSelector},
${liveChatIframeSelector},
${liveChatIframeWrapperSelector},
${liveChatBubbleSelector} {
  top: auto !important;
  bottom: ${liveChatDesktopOffset}px !important;
}

${liveChatIframeWrapperSelector},
${liveChatBubbleSelector} {
  transform-origin: ${transformOrigin} !important;
}

@media (max-width: ${liveChatLabMobileBreakpoint}px) {
  ${liveChatFloatingControlSelector},
  ${liveChatIframeSelector},
  ${liveChatIframeWrapperSelector},
  ${liveChatBubbleSelector} {
    top: auto !important;
    bottom: ${mobileBottomOffset} !important;
  }
}
`
  const fullScreenPanelPosition = `
@media (max-width: ${liveChatFullScreenBreakpoint}px) {
  ${liveChatFullScreenPanelSelector} {
    inset: 0 !important;
  }
}
`
  const mobileVisibility = props.hideOnLabMobile ? `
@media (max-width: ${liveChatLabMobileBreakpoint}px) {
  .live-chat-ai-wrapper,
  ${liveChatFloatingControlSelector},
  ${liveChatIframeSelector},
  ${liveChatIframeWrapperSelector},
  ${liveChatBubbleSelector} {
    display: none !important;
  }
}
` : ''

  return `
:host {
  z-index: ${liveChatRootZIndex} !important;
}

.live-chat-ai-wrapper {
  z-index: ${liveChatRootZIndex} !important;
}

${liveChatFloatingControlSelector},
${liveChatBubbleSelector} {
  z-index: ${liveChatButtonZIndex} !important;
}

${liveChatIframeWrapperSelector},
${liveChatIframeSelector} {
  z-index: ${liveChatPanelZIndex} !important;
}

${liveChatButtonSelector} {
  background: #000 !important;
  border-radius: 50% !important;
  box-shadow:
    0 8px 24px rgba(0,0,0,0.12),
    0 2px 8px rgba(0,0,0,0.25) !important;
  transition:
    transform 0.2s ease,
    box-shadow 0.2s ease,
    background 0.2s ease !important;
  background-image: url('${liveChatButtonIconUrl}') !important;
  background-repeat: no-repeat !important;
  background-position: center !important;
  background-size: 28px 28px !important;
}

${liveChatButtonContentSelector} {
  display: none !important;
}

${liveChatButtonHoverSelector} {
  transform: translateY(-2px);
  background-color: #111 !important;
  box-shadow:
    0 12px 32px rgba(0,0,0,0.18),
    0 4px 12px rgba(0,0,0,0.35) !important;
}

${horizontalPosition}
${verticalPosition}
${fullScreenPanelPosition}
${mobileVisibility}
`
}

function injectLiveChatButtonStyle(root: ShadowRoot, liveChatButtonCss: string): boolean {
  if (!root.querySelector(liveChatEmbedSelector)) {
    return false
  }

  let style = root.querySelector<HTMLStyleElement>('#' + liveChatButtonStyleId)

  if (style === null) {
    style = document.createElement('style')
    style.id = liveChatButtonStyleId
    root.appendChild(style)
  }

  if (style.textContent !== liveChatButtonCss) {
    style.textContent = liveChatButtonCss
  }

  return true
}

export default function LiveChatAiButtonStyles(props: Props) {
  const liveChatButtonCss = useMemo(() => {
    return getLiveChatButtonCss(props)
  }, [props.hideOnLabMobile, props.side, props.vertical])

  useEffect(() => {
    if (typeof MutationObserver === 'undefined') {
      return
    }

    const observedRoots = new WeakSet<Document | ShadowRoot>()
    const styledRoots = new WeakSet<ShadowRoot>()
    let observer: MutationObserver | null = null
    let scanScheduled = false
    let scanAttempts = 0
    const maxScanAttempts = 100
    let scanTimeout = 0
    let scanInterval = 0
    let pendingFullScan = false
    const pendingNodes = new Set<Node>()

    const stopPolling = () => {
      if (scanInterval === 0) {
        return
      }

      window.clearInterval(scanInterval)
      scanInterval = 0
    }

    const observeRoot = (root: Document | ShadowRoot) => {
      if (observedRoots.has(root) || observer === null) {
        return
      }

      observedRoots.add(root)
      observer.observe(root instanceof Document ? root.documentElement : root, {
        childList: true,
        subtree: true,
      })
    }

    const scanRoot = (root: Document | ShadowRoot): boolean => {
      observeRoot(root)

      let liveChatFound = false

      if (root instanceof ShadowRoot) {
        if (styledRoots.has(root)) {
          return true
        }

        liveChatFound = injectLiveChatButtonStyle(root, liveChatButtonCss)

        if (liveChatFound) {
          styledRoots.add(root)
        }
      }

      root.querySelectorAll('*').forEach((element) => {
        if (element.shadowRoot) {
          liveChatFound = scanRoot(element.shadowRoot) || liveChatFound
        }
      })

      return liveChatFound
    }

    const scanNode = (node: Node): boolean => {
      if (node instanceof Document || node instanceof ShadowRoot) {
        return scanRoot(node)
      }

      if (!(node instanceof Element)) {
        return false
      }

      let liveChatFound = false

      if (node.shadowRoot) {
        liveChatFound = scanRoot(node.shadowRoot)
      }

      node.querySelectorAll('*').forEach((element) => {
        if (element.shadowRoot) {
          liveChatFound = scanRoot(element.shadowRoot) || liveChatFound
        }
      })

      return liveChatFound
    }

    const scheduleScan = (nodes?: Iterable<Node>) => {
      if (nodes === undefined) {
        pendingFullScan = true
        pendingNodes.clear()
      } else if (!pendingFullScan) {
        for (const node of nodes) {
          pendingNodes.add(node)
        }
      }

      if (scanScheduled) {
        return
      }

      scanScheduled = true
      scanTimeout = window.setTimeout(() => {
        scanScheduled = false
        scanTimeout = 0

        const scanWholeDocument = pendingFullScan
        const nodesToScan = Array.from(pendingNodes)
        pendingFullScan = false
        pendingNodes.clear()

        let liveChatFound = false

        if (scanWholeDocument || nodesToScan.length === 0) {
          liveChatFound = scanRoot(document)
        } else {
          nodesToScan.forEach((node) => {
            liveChatFound = scanNode(node) || liveChatFound
          })
        }

        if (liveChatFound) {
          stopPolling()
        }
      }, 50)
    }

    observer = new MutationObserver((mutations) => {
      const addedNodes: Node[] = []

      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          addedNodes.push(node)
        })
      })

      if (addedNodes.length > 0) {
        scheduleScan(addedNodes)
      }
    })

    scanInterval = window.setInterval(() => {
      scanAttempts += 1
      scheduleScan()

      if (scanAttempts >= maxScanAttempts) {
        stopPolling()
      }
    }, 100)

    scheduleScan()

    return () => {
      observer.disconnect()
      stopPolling()

      if (scanTimeout !== 0) {
        window.clearTimeout(scanTimeout)
      }
    }
  }, [liveChatButtonCss])

  return <style id={liveChatButtonStyleId}>{liveChatButtonCss}</style>
}
